/**
 * Internationalization (i18n) Module
 * Handles language and currency management for the e-commerce system
 */

import sv from '../lang/sv.js';
import en from '../lang/en.js';
import nb from '../lang/nb.js';
import { getCurrency } from '../lang/currencies.js';

class I18n {
    constructor() {
        this.languages = { sv, en, nb };
        this.currentLanguage = 'sv';
        this.currentCurrency = 'SEK';
        this.translations = sv;
        this.currencyConfig = getCurrency('SEK');
    }

    /**
     * Set the current language
     * @param {string} lang - Language code (e.g., 'sv', 'en')
     */
    setLanguage(lang) {
        if (this.languages[lang]) {
            this.currentLanguage = lang;
            this.translations = this.languages[lang];
            console.log(`i18n: Language set to: ${lang}`);
        } else {
            console.warn(`i18n: Language "${lang}" not found, using default (sv)`);
        }
    }

    /**
     * Set the current currency
     * @param {string} currencyCode - Currency code (e.g., 'SEK', 'EUR', 'USD')
     */
    setCurrency(currencyCode) {
        this.currentCurrency = currencyCode;
        this.currencyConfig = getCurrency(currencyCode);
        console.log(`i18n: Currency set to: ${currencyCode}`);
    }

    /**
     * Get translation for a key
     * @param {string} key - Translation key in dot notation (e.g., 'cart.empty')
     * @returns {string} Translated string or the key itself if not found
     */
    t(key) {
        const keys = key.split('.');
        let value = this.translations;
        
        for (const k of keys) {
            value = value[k];
            if (value === undefined) {
                console.warn(`i18n: Translation key not found: "${key}"`);
                return key;
            }
        }
        
        return value;
    }

    /**
     * Format price with current currency settings
     * @param {number} price - Price to format
     * @returns {string} Formatted price string
     */
    formatPrice(price) {
        const cfg = this.currencyConfig;
        const num = parseFloat(price || 0);
        
        // Format number with decimals
        let formatted = num.toFixed(cfg.decimals);
        
        // Replace decimal separator
        formatted = formatted.replace('.', cfg.decimalSeparator);
        
        // Add thousand separator
        const parts = formatted.split(cfg.decimalSeparator);
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, cfg.thousandSeparator);
        formatted = parts.join(cfg.decimalSeparator);
        
        // Add currency symbol
        if (cfg.symbolPosition === 'before') {
            return `${cfg.symbol}${formatted}`;
        } else {
            return `${formatted} ${cfg.symbol}`;
        }
    }

    /**
     * Get current language code
     * @returns {string} Current language code
     */
    getLanguage() {
        return this.currentLanguage;
    }

    /**
     * Get current currency code
     * @returns {string} Current currency code
     */
    getCurrency() {
        return this.currentCurrency;
    }

    /**
     * Get currency configuration
     * @returns {Object} Currency configuration object
     */
    getCurrencyConfig() {
        return this.currencyConfig;
    }
}

// Create singleton instance
const i18n = new I18n();

// Export convenience functions
export function setLanguage(lang) {
    i18n.setLanguage(lang);
}

export function setCurrency(currency) {
    i18n.setCurrency(currency);
}

export function t(key) {
    return i18n.t(key);
}

export function formatPrice(price) {
    return i18n.formatPrice(price);
}

export function getLanguage() {
    return i18n.getLanguage();
}

export function getCurrencyCode() {
    return i18n.getCurrency();
}

export function getCurrencyConfig() {
    return i18n.getCurrencyConfig();
}

// Export the instance as default
export default i18n;
